import { Component, inject, OnDestroy, OnInit  } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, Observable, debounceTime, distinctUntilChanged, takeUntil, map, firstValueFrom } from 'rxjs';
import { Producto } from 'src/app/models/Producto';
import { ProductoOrdenComercio } from 'src/app/models/ProductoOrdenComercio';
import { Vademecum } from 'src/app/models/Vademecum';
import { VademecumOrdenComercio } from 'src/app/models/VademecumOrdenComercio';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { API_URI } from 'src/environments/environment';
import { normalizeText } from 'src/app/shared/text.util';

type SearchUiState = 'idle' | 'minChars' | 'showingResults';

@Component({
    selector: 'app-pedido',
    templateUrl: './pedido.component.html',
    styleUrls: ['./pedido.component.css'],
    standalone: false
})
export class PedidoComponent implements OnInit, OnDestroy {
  private readonly _router = inject(Router);
  private readonly _orderComercioService = inject(OrderComercioService);
  readonly _usuarioService = inject(UsuarioService); // si lo usás en template, dejalo público/readonly
  private readonly _authService = inject(AuthService);
  private readonly _loadingService = inject(LoadingService);

  API_URI = API_URI;

  error = '';
  loading = false;

  x_nombre_fantasia_comercio = this._orderComercioService.comercio?.x_nombre_fantasia ?? '';
  b_vende_medicamentos = !!this._orderComercioService.comercio?.b_vende_medicamentos;
  n_porcentaje_descuento_medicamentos = this._orderComercioService.comercio?.n_porcentaje_descuento_medicamentos ?? 0;

  productos: Producto[] = [];
  vademecums: Vademecum[] = [];

  vademecumsFiltrados$?: Observable<Vademecum[]>;
  searchTerm$ = new BehaviorSubject<string>('***');

  productosSeleccionados: ProductoOrdenComercio[] = [];
  vademecumsSeleccionados: VademecumOrdenComercio[] = [];

  n_cantidad_elementos = 0;
  minimaCantidadCaracteres = 3;

  // UI state (reemplaza DOM directo)
  cartOpen = false;
  helpOpen = false;

  isSearching = false;
  searchUiState: SearchUiState = 'idle';

  private readonly destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
        await this.initPantalla();
      } catch (e) {
        console.error(e);
        this._router.navigate(['/sign-in']);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initPantalla(): Promise<void> {
    this.filterListVademecum();

    this.productosSeleccionados = this._orderComercioService.productosOrdenComercio?.length
      ? this._orderComercioService.productosOrdenComercio
      : [];

    this.vademecumsSeleccionados = this._orderComercioService.vademecumsOrdenComercio?.length
      ? this._orderComercioService.vademecumsOrdenComercio
      : [];

    this.refreshCantidadElementos();

    const idComercio = this._orderComercioService.ordenComercio.id_comercio;

    if (!idComercio) {
      this.error = 'No se encontró el comercio seleccionado.';
      return;
    }

    try {
      const res = await firstValueFrom(
        this._usuarioService.getProductos(idComercio, true)
      );

      this.productos = res ?? [];

      console.log('Productos obtenidos:', this.productos);
    } catch (e) {
      console.error('Error al obtener los Productos Activos del Comercio', e);
      this.error = 'No se pudieron obtener los productos del comercio.';
    }
  }

  filterListVademecum(): void {
    this.vademecumsFiltrados$ = this.searchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      map((term) => {
        const t = normalizeText(term);

        if (t.length <= this.minimaCantidadCaracteres) return [];

        return this._orderComercioService.vademecums
          .filter((item) => {
            const nombre = normalizeText(item.x_nombre);
            const presentacion = normalizeText(item.x_presentacion);
            const laboratorio = normalizeText(item.nombreLaboratorio);
            const drogas = normalizeText(item.nombreDrogas);

            return (
              nombre.includes(t) ||
              presentacion.includes(t) ||
              laboratorio.includes(t) ||
              drogas.includes(t)
            );
          })
          .slice(0, 300);
      })
    );
  }

  // llamado desde el input (keyup)
  onSearchInput(value: string): void {
    const v = (value ?? '').trim();
    this.searchTerm$.next(v);

    if (v === '') {
      this.isSearching = false;
      this.searchUiState = 'idle';
      return;
    }

    this.isSearching = true;
    this.searchUiState = v.length <= this.minimaCantidadCaracteres ? 'minChars' : 'showingResults';
  }

  // UI
  openmodal(): void {
    this.cartOpen = !this.cartOpen;
  }

  openhelp(): void {
    this.helpOpen = !this.helpOpen;
  }

  // ===== Vademécums =====
  addVademecumCart(v: Vademecum): void {
    const found = this.vademecumsSeleccionados.find((e) => e.id_vademecum === v.id);
    if (!found) {
      const item: VademecumOrdenComercio = {
        id_vademecum: v.id,
        n_cantidad: 1,
        n_precio: v.n_precio!,
        n_precio_final: v.n_precio!,
        n_porcentaje_descuento: this.n_porcentaje_descuento_medicamentos,
        x_nombre: v.x_nombre!,
        x_presentacion: v.x_presentacion!,
        nombreLaboratorio: v.nombreLaboratorio!,
      };
      this.vademecumsSeleccionados.push(item);
    } else {
      found.n_cantidad = (found.n_cantidad ?? 0) + 1;
    }
    this.refreshCantidadElementos();
  }

  addQtyVademecumCart(v: VademecumOrdenComercio): void {
    const found = this.vademecumsSeleccionados.find((e) => e.id_vademecum === v.id_vademecum);
    if (found) found.n_cantidad = (found.n_cantidad ?? 0) + 1;
    this.refreshCantidadElementos();
  }

  isActiveVademecum(id: number): boolean {
    return this.vademecumsSeleccionados.some((e) => e.id_vademecum === id);
  }

  removeVademecumCart(v: VademecumOrdenComercio): void {
    const found = this.vademecumsSeleccionados.find((e) => e.id_vademecum === v.id_vademecum);
    if (!found) return;

    if ((found.n_cantidad ?? 0) > 1) found.n_cantidad = (found.n_cantidad ?? 0) - 1;
    else this.vademecumsSeleccionados = this.vademecumsSeleccionados.filter((e) => e.id_vademecum !== v.id_vademecum);

    this.refreshCantidadElementos();
  }

  // ===== Productos =====
  addProductoCart(p: Producto): void {
    const found = this.productosSeleccionados.find((e) => e.id_producto === p.id);
    if (!found) {
      const item: ProductoOrdenComercio = {
        id_producto: p.id,
        n_cantidad: 1,
        n_precio: p.n_precio!,
        n_precio_final: p.n_precio!,
        n_porcentaje_descuento: p.n_porcentaje_descuento!,
        x_nombre: p.x_nombre!,
        x_sku: p.x_sku!,
        x_img_producto: p.x_img_producto!,
      };
      this.productosSeleccionados.push(item);
    } else {
      found.n_cantidad = (found.n_cantidad ?? 0) + 1;
    }
    this.refreshCantidadElementos();
  }

  addQtyProductoCart(p: ProductoOrdenComercio): void {
    const found = this.productosSeleccionados.find((e) => e.id_producto === p.id_producto);
    if (found) found.n_cantidad = (found.n_cantidad ?? 0) + 1;
    this.refreshCantidadElementos();
  }

  isActiveProducto(id: number): boolean {
    return this.productosSeleccionados.some((e) => e.id_producto === id);
  }

  qtyProducto(id: number): number | '' {
    const found = this.productosSeleccionados.find((e) => e.id_producto === id);
    return found ? (found.n_cantidad ?? 0) : '';
  }

  removeProductoCart(p: Producto): void {
    const found = this.productosSeleccionados.find((e) => e.id_producto === p.id);
    if (!found) return;

    if ((found.n_cantidad ?? 0) > 1) found.n_cantidad = (found.n_cantidad ?? 0) - 1;
    else this.productosSeleccionados = this.productosSeleccionados.filter((e) => e.id_producto !== p.id);

    this.refreshCantidadElementos();
  }

  removeProductoOrdenComercioCart(p: ProductoOrdenComercio): void {
    const found = this.productosSeleccionados.find((e) => e.id_producto === p.id_producto);
    if (!found) return;

    if ((found.n_cantidad ?? 0) > 1) found.n_cantidad = (found.n_cantidad ?? 0) - 1;
    else this.productosSeleccionados = this.productosSeleccionados.filter((e) => e.id_producto !== p.id_producto);

    this.refreshCantidadElementos();
  }

  refreshCantidadElementos(): void {
    const totalV = this.vademecumsSeleccionados.reduce((acc, v) => acc + (v.n_cantidad ?? 0), 0);
    const totalP = this.productosSeleccionados.reduce((acc, p) => acc + (p.n_cantidad ?? 0), 0);
    this.n_cantidad_elementos = totalV + totalP;
  }

  savePedido(): void {
    this.loading = true;

    if (this.productosSeleccionados.length === 0 && this.vademecumsSeleccionados.length === 0) {
      this.error = 'Debe seleccionar un producto';
      this.loading = false;
      return;
    }

    this._orderComercioService.productosOrdenComercio = this.productosSeleccionados;
    this._orderComercioService.vademecumsOrdenComercio = this.vademecumsSeleccionados;
    this._router.navigate(['/condiciones']);
  }
}
